import { useSelect } from "downshift";
import { useState, ComponentType } from "react";
import type {
  SelectBoxItem,
  ItemRenderProps,
  DisabledItemRenderProps,
  SelectBoxComponents,
} from "./types";

export interface SelectBoxInput {
  items: SelectBoxItem[];
  hashFunction: (item: SelectBoxItem) => string | number;
  ItemRenderComponent: ComponentType<ItemRenderProps>;
  onChangeEvent?: (args: { value: string | number }) => void;
  matchSelectedItem: SelectBoxItem | undefined;
  isItemDisabled?: (item: SelectBoxItem) => boolean;
  renderDisabledItem?: ComponentType<DisabledItemRenderProps>;
  components: SelectBoxComponents;
}

const SelectBox = ({
  items,
  hashFunction,
  ItemRenderComponent,
  onChangeEvent,
  matchSelectedItem,
  isItemDisabled,
  renderDisabledItem: RenderDisabledItem,
  components: {
    SelectBoxContainer,
    DropdownContainer,
    ToggleButton,
    ArrowUp,
    ArrowDown,
    MainMenu,
    SelectedItemLabel,
  },
}: SelectBoxInput) => {
  function ComboBox() {
    const [displayItems, setDisplayItems] = useState(items);

    const {
      getToggleButtonProps,
      getLabelProps,
      getMenuProps,
      getItemProps,
      selectedItem,
      highlightedIndex,
      isOpen,
      selectItem,
    } = useSelect({
      onSelectedItemChange({ selectedItem }) {
        if (selectedItem) {
          if (
            !matchSelectedItem ||
            matchSelectedItem.value !== selectedItem.value
          ) {
            onChangeEvent?.({ value: selectedItem.value });
          }
        }
      },
      items: displayItems,
      itemToString(item) {
        return item ? String(hashFunction(item)) : "";
      },
    });

    if (matchSelectedItem && !selectedItem) {
      selectItem(matchSelectedItem);
    }

    return (
      <SelectBoxContainer>
        <DropdownContainer
          aria-label="toggle menu"
          role="button"
          {...getToggleButtonProps()}
          className={isOpen ? "dropdown-open" : ""}
        >
          <SelectedItemLabel {...getLabelProps()}>
            {selectedItem ? selectedItem.label : ""}
          </SelectedItemLabel>
          <ToggleButton>
            {isOpen ? <ArrowUp /> : <ArrowDown />}
          </ToggleButton>
        </DropdownContainer>
        <MainMenu
          {...getMenuProps()}
          style={{ display: isOpen ? "block" : "none" }}
        >
          {isOpen &&
            displayItems.map((item, index) => {
              if (
                isItemDisabled &&
                RenderDisabledItem &&
                isItemDisabled(item)
              ) {
                return (
                  <RenderDisabledItem
                    key={`dropdown_item_${item.value}`}
                    item={item}
                    hashFunction={hashFunction}
                  />
                );
              }
              return (
                <ItemRenderComponent
                  key={`dropdown_item_${item.value}`}
                  item={item}
                  index={index}
                  highlightedIndex={highlightedIndex}
                  selectedItem={selectedItem}
                  getItemProps={getItemProps}
                  hashFunction={hashFunction}
                  displayItems={displayItems}
                  setDisplayItems={setDisplayItems}
                />
              );
            })}
        </MainMenu>
      </SelectBoxContainer>
    );
  }
  return <ComboBox />;
};

export default SelectBox;
